/**
 * GovernanceSLAPanel — admin config for SLA timers + escalation + audit trail.
 * Intended for use inside AdminSettings.
 */
import { useState, useEffect } from 'react'
import { Shield, Clock, AlertTriangle, Download, ChevronDown, ChevronUp } from 'lucide-react'
import {
  governanceSlaService,
  type SLAConfig,
  type OverdueChangeRequest,
  type AuditLogEntry,
  type AuditTrailFilters,
} from '../services/governanceSla'
import { toast } from 'sonner'
import { DateDisplay } from './ui/DateDisplay'

// ── SLA Config section ────────────────────────────────────────────────────────

function SLAConfigSection() {
  const [, setConfig] = useState<SLAConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    change_request_sla_hours: 48,
    compliance_threshold: 0.70,
    block_on_overdue_review: false,
    escalation_user_id: '' as string | number,
  })

  useEffect(() => { load() }, [])

  const load = async () => {
    try {
      const data = await governanceSlaService.getConfig()
      setConfig(data)
      setForm({
        change_request_sla_hours: data.change_request_sla_hours,
        compliance_threshold: data.compliance_threshold,
        block_on_overdue_review: data.block_on_overdue_review,
        escalation_user_id: data.escalation_user_id ?? '',
      })
    } catch {
      toast.error('Failed to load SLA config')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await governanceSlaService.updateConfig({
        change_request_sla_hours: Number(form.change_request_sla_hours),
        compliance_threshold: Number(form.compliance_threshold),
        block_on_overdue_review: form.block_on_overdue_review,
        escalation_user_id: form.escalation_user_id ? Number(form.escalation_user_id) : undefined,
      })
      setConfig(updated)
      toast.success('SLA configuration saved')
    } catch {
      toast.error('Failed to save SLA config')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-sm text-gray-500 py-4">Loading…</div>

  return (
    <form onSubmit={handleSave} className="space-y-4" aria-label="SLA configuration">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="sla-hours" className="block text-xs font-medium text-gray-700 mb-1">
            Change request SLA (hours)
          </label>
          <input
            id="sla-hours"
            type="number"
            min={1}
            max={720}
            value={form.change_request_sla_hours}
            onChange={e => setForm(f => ({ ...f, change_request_sla_hours: parseInt(e.target.value) || 48 }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
            aria-describedby="sla-hours-hint"
          />
          <p id="sla-hours-hint" className="text-[11px] text-gray-400 mt-1">Default: 48 hours</p>
        </div>

        <div>
          <label htmlFor="sla-threshold" className="block text-xs font-medium text-gray-700 mb-1">
            Compliance threshold
          </label>
          <div className="relative">
            <input
              id="sla-threshold"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.compliance_threshold}
              onChange={e => setForm(f => ({ ...f, compliance_threshold: parseFloat(e.target.value) || 0.70 }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Alert when compliance drops below {Math.round(form.compliance_threshold * 100)}%
          </p>
        </div>

        <div>
          <label htmlFor="sla-escalation" className="block text-xs font-medium text-gray-700 mb-1">
            Escalation user ID <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="sla-escalation"
            type="number"
            value={form.escalation_user_id}
            onChange={e => setForm(f => ({ ...f, escalation_user_id: e.target.value }))}
            placeholder="User ID to escalate to"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
          />
        </div>

        <div className="flex items-start gap-3 pt-5">
          <input
            id="sla-block"
            type="checkbox"
            checked={form.block_on_overdue_review}
            onChange={e => setForm(f => ({ ...f, block_on_overdue_review: e.target.checked }))}
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="sla-block" className="text-sm text-gray-700 cursor-pointer">
            Block milestone completion when governance review is overdue
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="px-4 py-2 bg-[#486581] text-white text-sm rounded-lg hover:bg-[#3d5570] disabled:opacity-50 transition-colors min-h-[40px]"
      >
        {saving ? 'Saving…' : 'Save configuration'}
      </button>
    </form>
  )
}

// ── Overdue approvals section ─────────────────────────────────────────────────

function OverdueApprovalsSection() {
  const [items, setItems] = useState<OverdueChangeRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [escalating, setEscalating] = useState(false)

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    try {
      const data = await governanceSlaService.getOverdueRequests()
      setItems(data)
    } catch {
      toast.error('Failed to load overdue requests')
    } finally {
      setLoading(false)
    }
  }

  const handleEscalateAll = async () => {
    if (!confirm('Escalate all overdue change requests to the configured escalation user?')) return
    setEscalating(true)
    try {
      const result = await governanceSlaService.escalateOverdue()
      toast.success(`Escalated ${result.escalated} request${result.escalated !== 1 ? 's' : ''}`)
      load()
    } catch {
      toast.error('Failed to escalate')
    } finally {
      setEscalating(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" aria-hidden="true" />
          <span className="text-sm font-medium text-gray-700">Overdue approvals</span>
          {items.length > 0 && (
            <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded-full font-medium">
              {items.length}
            </span>
          )}
        </div>
        {items.length > 0 && (
          <button
            onClick={handleEscalateAll}
            disabled={escalating}
            className="px-3 py-1.5 text-xs bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors min-h-[32px]"
          >
            {escalating ? 'Escalating…' : 'Escalate all'}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">No overdue approvals — you're all caught up.</p>
      ) : (
        <ul className="space-y-2" role="list" aria-label="Overdue change requests">
          {items.map(item => (
            <li key={item.id} className="flex items-center justify-between border border-orange-100 bg-orange-50/40 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm font-medium text-neutral-950">{item.title}</p>
                <p className="text-xs text-gray-500 capitalize">{item.request_type} · Program {item.program_id}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-xs font-semibold text-red-600">
                  {item.hours_overdue.toFixed(0)}h overdue
                </span>
                {item.escalated_at && (
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Escalated <DateDisplay date={item.escalated_at} />
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Audit trail section ───────────────────────────────────────────────────────

function AuditTrailSection() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<AuditTrailFilters>({ limit: 100 })
  const [filtersOpen, setFiltersOpen] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const data = await governanceSlaService.getAuditTrail(filters)
      setEntries(data)
    } catch {
      toast.error('Failed to load audit trail')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleExportCSV = () => {
    const rows = [
      ['ID', 'Date', 'User ID', 'Entity', 'Entity ID', 'Action', 'Description'],
      ...entries.map(e => [
        e.id, e.created_at, e.user_id ?? '', e.entity_type, e.entity_id ?? '', e.action, e.description ?? '',
      ]),
    ]
    const csv = rows.map(r => r.map(String).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setFiltersOpen(v => !v)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
          aria-expanded={filtersOpen}
        >
          Filters
          {filtersOpen ? <ChevronUp className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[32px]"
          >
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            disabled={entries.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors min-h-[32px]"
          >
            <Download className="w-3 h-3" aria-hidden="true" />
            Export CSV
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-3 gap-3">
          {[
            { id: 'af-entity', label: 'Entity type', key: 'entity_type', placeholder: 'e.g. program' },
            { id: 'af-action', label: 'Action', key: 'action', placeholder: 'e.g. escalated' },
          ].map(({ id, label, key, placeholder }) => (
            <div key={key}>
              <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
              <input
                id={id}
                type="text"
                value={(filters as any)[key] || ''}
                onChange={e => setFilters(f => ({ ...f, [key]: e.target.value || undefined }))}
                placeholder={placeholder}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[36px]"
              />
            </div>
          ))}
          <div className="flex items-end">
            <button
              onClick={load}
              className="px-3 py-1.5 bg-[#486581] text-white text-xs rounded-lg hover:bg-[#3d5570] transition-colors min-h-[36px] w-full"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">No audit log entries found</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" role="table" aria-label="Audit trail">
            <thead>
              <tr className="border-b border-gray-200 text-left bg-gray-50">
                <th className="py-2 px-3 text-xs font-medium text-gray-600 whitespace-nowrap">Date</th>
                <th className="py-2 px-3 text-xs font-medium text-gray-600">Entity</th>
                <th className="py-2 px-3 text-xs font-medium text-gray-600">Action</th>
                <th className="py-2 px-3 text-xs font-medium text-gray-600">Description</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 px-3 whitespace-nowrap">
                    <DateDisplay date={e.created_at} className="text-xs text-gray-500" />
                  </td>
                  <td className="py-2.5 px-3 text-gray-700 whitespace-nowrap text-xs">
                    <span className="capitalize">{e.entity_type}</span>
                    {e.entity_id ? <span className="text-gray-400"> #{e.entity_id}</span> : null}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200 capitalize">
                      {e.action}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-xs text-gray-600 max-w-xs truncate">{e.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

interface SectionProps { open: boolean; onToggle: () => void; title: string; icon: React.ReactNode; children: React.ReactNode }

function Collapsible({ open, onToggle, title, icon, children }: SectionProps) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {icon}
          {title}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />}
      </button>
      {open && <div className="px-5 py-5 border-t border-gray-100">{children}</div>}
    </div>
  )
}

export function GovernanceSLAPanel() {
  const [openSection, setOpenSection] = useState<string>('config')
  const toggle = (s: string) => setOpenSection(prev => prev === s ? '' : s)

  return (
    <div className="space-y-3">
      <Collapsible
        open={openSection === 'config'}
        onToggle={() => toggle('config')}
        title="SLA Configuration"
        icon={<Clock className="w-4 h-4 text-[#486581]" aria-hidden="true" />}
      >
        <SLAConfigSection />
      </Collapsible>

      <Collapsible
        open={openSection === 'overdue'}
        onToggle={() => toggle('overdue')}
        title="Overdue Approvals"
        icon={<AlertTriangle className="w-4 h-4 text-orange-500" aria-hidden="true" />}
      >
        <OverdueApprovalsSection />
      </Collapsible>

      <Collapsible
        open={openSection === 'audit'}
        onToggle={() => toggle('audit')}
        title="Audit Trail"
        icon={<Shield className="w-4 h-4 text-[#486581]" aria-hidden="true" />}
      >
        <AuditTrailSection />
      </Collapsible>
    </div>
  )
}
